import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { AuditTimeline, type AuditEntry } from "@/components/admin/shared";
import type {
  OrderPayment,
  OrderTimelineEvent,
} from "@/lib/admin-api/orders";
import { useOrderTimeline } from "@/hooks/admin/useOrders";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface OrderTimelineProps {
  /**
   * The order ID whose timeline we should display. When null the
   * component renders the payment-only view (loading placeholder
   * while the parent fetches). Must come from the parent so the
   * hook only fires once per mount.
   */
  readonly orderId: string | null;
  readonly payments: readonly OrderPayment[];
  readonly isLoading: boolean;
}

// ---------------------------------------------------------------------------
// Event mappers
// ---------------------------------------------------------------------------

function paymentsToAuditEntries(
  payments: readonly OrderPayment[],
): readonly AuditEntry[] {
  return payments.map((payment) => ({
    id: `pay:${payment.id}`,
    action: `payment:${payment.status}`,
    user: payment.method,
    timestamp: new Date(payment.created_at),
    details: {
      amount: payment.amount,
      reference: payment.reference,
      status: payment.status,
    },
  }));
}

function eventsToAuditEntries(
  events: readonly OrderTimelineEvent[],
): readonly AuditEntry[] {
  return events.map((event) => ({
    // Prefix IDs so payment rows and timeline rows never collide.
    id: `evt:${event.id}`,
    // `kind:summary` keeps the badge + text both readable. The
    // AuditTimeline's ACTION_COLORS will fall back to a neutral
    // colour for unknown kinds.
    action: `${event.kind}:${event.summary}`,
    user: event.source,
    timestamp: new Date(event.created_at),
    details: event.metadata ?? undefined,
  }));
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function OrderTimeline({
  orderId,
  payments,
  isLoading,
}: OrderTimelineProps) {
  const { t } = useTranslation("admin");

  const { data: timelineData, isLoading: timelineLoading } =
    useOrderTimeline(orderId);

  const entries = useMemo<readonly AuditEntry[]>(() => {
    const paymentEntries = paymentsToAuditEntries(payments);
    const eventEntries = eventsToAuditEntries(timelineData?.events ?? []);
    // Merge + sort newest first. Both sources already arrive in DESC
    // order individually, but after merging we cannot trust that.
    return [...paymentEntries, ...eventEntries].sort(
      (a, b) => b.timestamp.getTime() - a.timestamp.getTime(),
    );
  }, [payments, timelineData?.events]);

  const combinedLoading = isLoading || timelineLoading;

  return (
    <div className="rounded-xl border border-stone-200 bg-white">
      <div className="flex items-center justify-between border-b border-stone-100 px-5 py-4">
        <h3 className="font-display text-base font-semibold text-stone-900">
          {t("order_detail.timeline")}
        </h3>
        {timelineData?.warning && (
          // Shown when migration 122 has not been applied yet. Keeps
          // the panel useful (payments still render) while signalling
          // to operators that structured status history is coming.
          <span
            role="status"
            className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-800"
            title={timelineData.warning.message}
          >
            Historique partiel
          </span>
        )}
      </div>
      <div className="p-5">
        <AuditTimeline entries={entries} isLoading={combinedLoading} />
      </div>
    </div>
  );
}
